import { FileText, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Skeleton } from '~/components/ui/skeleton';

import { PICK_PENDING, type FileLayoutState } from '../../hooks/use-file-layout';
import type { ResolvedTransformer } from '../../types/transformer.types';
import { formatLabel, isStandardResolution } from '../../utils/transformer-display';
import { TransformerCallout } from '../transformer-callout';
import { TransformerPicker } from '../transformer-picker';

interface Props {
  layout: FileLayoutState;
  /** `export`: assigned layout vs "use another"; `delivery`: every assigned file vs only one. */
  mode: 'export' | 'delivery';
  pickedId: string | null;
  onPick: (id: string | null) => void;
  /** Unique prefix for radio ids (several dialogs can be mounted at once). */
  idPrefix: string;
  /** Subsidiary name for the customer read-only note. */
  subsidiaryName?: string | undefined;
  /** E2E `data-testid` root; controls derive `${testId}-...`. */
  testId?: string;
}

/**
 * The "File layout" control shared by the Export sheet and both delivery dialogs.
 * Lily users (super admin or `@lily.ai`) may override resolution with another
 * transformer; customers see what applies, read-only. Every file is listed with
 * the resolver's own sentence for why it applies.
 */
export function FileLayoutChoice({
  layout,
  mode,
  pickedId,
  onPick,
  idPrefix,
  subsidiaryName,
  testId = 'file-layout-choice',
}: Props) {
  const { t } = useTranslation('transformers');

  if (layout.isLoading)
    return <Skeleton data-testid={`${testId}-loading`} className="h-16 w-full max-w-xl" />;
  // Resolve failed for a reason other than permissions: the host blocks the export /
  // delivery until a retry succeeds, rather than quietly sending the standard file.
  if (layout.failed) {
    return (
      <TransformerCallout testId={`${testId}-error`} tone="error">
        <span className="flex flex-wrap items-center gap-2">
          <span>{t('layout.loadFailed')}</span>
          <Button
            data-testid={`${testId}-retry-button`}
            type="button"
            variant="outline"
            size="sm"
            className="cursor-pointer"
            isLoading={layout.isRetrying}
            onClick={layout.retry}
          >
            {t('layout.retry')}
          </Button>
        </span>
      </TransformerCallout>
    );
  }
  // 403: this user can't see layouts, so the standard export is what runs.
  if (layout.forbidden) {
    return (
      <p data-testid={`${testId}-standard`} className="text-muted-foreground text-sm">
        {t('layout.standard')}
      </p>
    );
  }
  if (layout.resolved.length === 0) {
    return (
      <p data-testid={`${testId}-unavailable`} className="text-muted-foreground text-sm">
        {t('layout.unavailable')}
      </p>
    );
  }

  const first = layout.resolved[0];
  const resolvedIds = new Set(layout.resolved.map((r) => r.transformerId));
  // In export mode "another" means anything but what already applies.
  const options =
    mode === 'export' ? layout.library.filter((tr) => !resolvedIds.has(tr.id)) : layout.library;
  const multiple = layout.resolved.length > 1;

  const fileList = (
    <ul data-testid={`${testId}-files`} className="flex flex-col gap-1.5">
      {layout.resolved.map((r) => (
        <ResolvedRow
          key={`${r.transformerId ?? 'standard'}-${r.name}`}
          testId={`${testId}-file-${r.transformerId ?? 'standard'}`}
          item={r}
        />
      ))}
    </ul>
  );
  const clashNote = layout.clashes.length > 0 && (
    <TransformerCallout testId={`${testId}-clash`} tone="error">
      {t('layout.clash', { name: layout.clashes[0] ?? '' })}
    </TransformerCallout>
  );

  if (!layout.canChoose) {
    return (
      <div data-testid={`${testId}-read-only`} className="flex flex-col gap-2">
        <div className="flex items-start gap-2.5">
          <Lock aria-hidden="true" className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex min-w-0 flex-col gap-1.5">
            {fileList}
            <span className="text-muted-foreground text-xs">
              {isStandardResolution(first)
                ? (first?.reasonText ?? '')
                : t('layout.customerNote', { name: subsidiaryName ?? t('layout.yourSubsidiary') })}
            </span>
          </div>
        </div>
        {clashNote}
      </div>
    );
  }

  const picking = pickedId !== null;
  // Nothing assigned: one "Standard export" choice described by the resolver's
  // sentence, rather than a file row repeating the name with an empty format pill.
  const standardOnly = layout.standard;

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <RadioGroup
        data-testid={`${testId}-radio-group`}
        value={picking ? 'pick' : 'resolved'}
        onValueChange={(v) => {
          // No preselection: a silently chosen first transformer is easy to export by
          // mistake. The host blocks submit until one is picked (`layout.pickPending`).
          onPick(v === 'pick' ? PICK_PENDING : null);
        }}
        className="gap-3"
      >
        <div className="flex items-start gap-2.5">
          <RadioGroupItem
            data-testid={`${testId}-resolved-radio`}
            value="resolved"
            id={`${idPrefix}-resolved`}
            className="size-5 cursor-pointer"
          />
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label
              htmlFor={`${idPrefix}-resolved`}
              className="flex min-h-5 cursor-pointer flex-wrap items-center gap-2 leading-5 font-medium"
            >
              {standardOnly
                ? t('layout.standard')
                : mode === 'delivery'
                  ? t('layout.sendAll')
                  : t('layout.assigned')}
              {mode === 'delivery' && multiple && (
                <Badge variant="outline" className="text-[11px] font-normal">
                  {t('layout.files', { count: layout.resolved.length })}
                </Badge>
              )}
            </Label>
            {standardOnly ? (
              <span className="text-muted-foreground text-xs">{first?.reasonText ?? ''}</span>
            ) : (
              fileList
            )}
          </div>
        </div>
        <div className="flex items-start gap-2.5">
          <RadioGroupItem
            data-testid={`${testId}-pick-radio`}
            value="pick"
            id={`${idPrefix}-pick`}
            disabled={options.length === 0}
            className="size-5 cursor-pointer disabled:cursor-not-allowed"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-pick`} className="cursor-pointer leading-5 font-medium">
              {mode === 'delivery' ? t('layout.sendOne') : t('layout.useAnother')}
            </Label>
            {picking && (
              <TransformerPicker
                testId={`${testId}-picker`}
                transformers={options}
                value={pickedId}
                onChange={onPick}
                ariaLabel={t('layout.pickLabel')}
                placeholder={t('layout.pickPlaceholder')}
                className="max-w-md"
              />
            )}
            {picking && layout.picked?.description && (
              <span className="text-muted-foreground text-xs">{layout.picked.description}</span>
            )}
          </div>
        </div>
      </RadioGroup>
      {!picking && clashNote}
      <span className="text-muted-foreground flex items-center gap-1 text-xs">
        <Lock aria-hidden="true" className="h-3 w-3" />
        {t('layout.lilyOnly')}
      </span>
    </div>
  );
}

/**
 * One resolved file: name, format / version badges, the resolver's reason and any
 * file names. The standard item has no format or version, so it shows neither pill.
 */
export function ResolvedRow({
  item,
  testId = 'resolved-row',
}: {
  item: ResolvedTransformer;
  /** E2E `data-testid` for the row; name/reason derive `${testId}-...`. */
  testId?: string;
}) {
  return (
    <li data-testid={testId} className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-1.5 text-sm">
        <FileText aria-hidden="true" className="text-muted-foreground h-3.5 w-3.5" />
        <span data-testid={`${testId}-name`} className="font-medium">
          {item.name}
        </span>
        {item.format && (
          <Badge variant="secondary" className="pointer-events-none text-[11px] font-normal">
            {formatLabel(item.format)}
          </Badge>
        )}
        {item.version !== null && (
          <Badge variant="outline" className="text-[11px] font-normal">
            v{item.version}
          </Badge>
        )}
      </span>
      <span data-testid={`${testId}-reason`} className="text-muted-foreground text-xs">
        {item.reasonText}
      </span>
      {item.fileNames.map((name) => (
        <span key={name} className="font-mono text-xs break-all">
          {name}
        </span>
      ))}
    </li>
  );
}
